"use client"

/* eslint-disable indent, prefer-smart-quotes/prefer, no-trailing-spaces */

import clsx from "clsx"
import React from "react"

import { scroller } from "@/hooks/useScroll"

import ArrowLeft from "../../../../public/icons/arrow-left.svg" // Adjust path as needed
import ArrowRight from "../../../../public/icons/arrow-right.svg" // Adjust path as needed

interface PaginationProps {
  totalPages: number
  currentPage: number
  onPageChange: (page: number) => void
  scrollTo?: string // Optional scrollTo parameter
}

const ClientSidePagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  onPageChange,
  scrollTo,
}) => {
  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 9

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push({ type: "page", value: i })
      }
    } else if (currentPage <= 2) {
      for (let i = 1; i <= 3; i++) {
        pages.push({ type: "page", value: i })
      }
      pages.push({ type: "ellipsis", key: "ellipsis-1" })
      pages.push({ type: "page", value: totalPages - 1 })
      pages.push({ type: "page", value: totalPages })
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push({ type: "page", value: i })
      }
      pages.push({ type: "ellipsis", key: "ellipsis-1" })
      pages.push({ type: "page", value: totalPages - 1 })
      pages.push({ type: "page", value: totalPages })
    } else if (currentPage >= totalPages - 3) {
      pages.push({ type: "page", value: 1 })
      pages.push({ type: "page", value: 2 })
      pages.push({ type: "ellipsis", key: "ellipsis-1" })
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push({ type: "page", value: i })
      }
    } else {
      pages.push({ type: "page", value: 1 })
      pages.push({ type: "page", value: 2 })
      pages.push({ type: "ellipsis", key: "ellipsis-1" })
      pages.push({ type: "page", value: currentPage - 1 })
      pages.push({ type: "page", value: currentPage })
      pages.push({ type: "page", value: currentPage + 1 })
      pages.push({ type: "ellipsis", key: "ellipsis-2" })
      pages.push({ type: "page", value: totalPages - 1 })
      pages.push({ type: "page", value: totalPages })
    }

    return pages
  }

  const pages = renderPageNumbers()

  const handleClick = () => {
    if (scrollTo) {
      scroller.scrollTo(scrollTo, {
        duration: 1000,
        delay: 0,
        smooth: true,
      })
    }
  }

  return (
    <div className="flex items-center justify-center w-full text-sm select-none border-t border-[#394046] relative overflow-visible">
      {currentPage > 1 ? (
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => {
            onPageChange(currentPage - 1)
            handleClick()
          }}
          className={clsx(
            "flex items-center mr-8 text-gray-2.5 hover:text-gray-2.5 focus:outline-none cursor-pointer",
          )}
        >
          <ArrowLeft className="mr-8 w-3 block h-4.5 text-white-100" />
        </button>
      ) : (
        <div className="flex items-center mr-8 text-gray-30 cursor-not-allowed">
          <ArrowLeft className="mr-8 w-3 block h-4.5 text-white-100" />
        </div>
      )}

      <div className="flex items-center justify-center">
        {pages.map((pageObj) =>
          pageObj.type === "ellipsis" ? (
            <div
              key={pageObj.key}
              className="w-10 px-0.5 text-center text-white-100"
            >
              ...
            </div>
          ) : (
            <button
              key={pageObj.value}
              type="button"
              aria-label={`Page ${pageObj.value}`}
              onClick={() => {
                onPageChange(pageObj.value)
                handleClick()
              }}
              className={clsx(
                "flex items-center justify-center w-10 h-18 text-lg cursor-pointer relative",
                currentPage === pageObj.value
                  ? 'text-gray-2.5 bg-opacity-0 before:absolute before:-top-[2px] before:left-1/2 before:-translate-x-1/2 before:w-full before:h-[2px] before:bg-[#008CFF] before:content-[""] before:z-10 mt-[-3px]'
                  : "text-gray-2.5 opacity-50",
              )}
            >
              {pageObj.value}
            </button>
          ),
        )}
      </div>

      {currentPage < totalPages ? (
        <button
          type="button"
          aria-label="Next page"
          onClick={() => {
            onPageChange(currentPage + 1)
            handleClick()
          }}
          className={clsx(
            "flex items-center ml-8 text-gray-2.5 hover:text-gray-2.5 focus:outline-none cursor-pointer",
          )}
        >
          <ArrowRight className="ml-8 w-3 block h-4.5 text-white-100" />
        </button>
      ) : (
        <div className="flex items-center ml-8 text-gray-30 cursor-not-allowed">
          <ArrowRight className="w-3 block h-4.5 text-white-100" />
        </div>
      )}
    </div>
  )
}

export default ClientSidePagination
